import { isPriceVisible, isPurchasable } from "@/domain/catalog/online-status";
import {
	type CatalogProductTag,
	type CatalogProduct,
	type ProductImage,
	type Locale
} from "@/domain/catalog/product";

const CURRENCY_BY_LOCALE: Record<Locale, string> = {
	"en-US": "USD",
	"en-CA": "CAD"
};

const MISSING_IMAGE_SRC = "";

export interface ListingProductPrice {
	display: number;
	strikeThrough?: number;
	currency: string;
	locale: Locale;
}

export interface ListingProductRating {
	value: number;
	count: number;
}

export interface ListingProduct {
	sku: string;
	title: string;
	href: string;
	image: { src: string; alt: string };
	price?: ListingProductPrice;
	rating?: ListingProductRating;
	highlightTag?: CatalogProductTag;
	isPurchasable: boolean;
}

function isWithinWindow(tag: CatalogProductTag): boolean {
	const now = Date.now();
	if (tag.startDate && Date.parse(tag.startDate) > now) return false;
	if (tag.endDate && Date.parse(tag.endDate) < now) return false;
	return true;
}

// One promotional badge per card — the grid has no room for a wrapping row.
function pickHighlightTag(
	product: CatalogProduct
): CatalogProductTag | undefined {
	return (product.productTags ?? []).find(
		(tag) => tag.kind === "highlight" && isWithinWindow(tag)
	);
}

function pickImage(product: CatalogProduct): ProductImage | undefined {
	if (product.lifestyleImage?.url) return product.lifestyleImage;
	if (product.heroImage?.url) return product.heroImage;

	for (const asset of product.mediaGallery ?? []) {
		if (asset.type === "image" && asset.url) {
			return { type: "image", url: asset.url, altText: asset.altText };
		}
	}

	return undefined;
}

function pickPrice(
	product: CatalogProduct,
	locale: Locale
): ListingProductPrice | undefined {
	if (!isPriceVisible(product.onlineStatus)) return undefined;

	const display = product.salePrice ?? product.listPrice;
	if (typeof display !== "number") return undefined;

	const list = product.listPrice;
	const isMarkedDown = typeof list === "number" && display < list;

	return {
		display,
		strikeThrough: isMarkedDown ? list : undefined,
		currency: CURRENCY_BY_LOCALE[locale],
		locale
	};
}

function pickRating(product: CatalogProduct): ListingProductRating | undefined {
	const { starsRating, numRatings } = product;

	if (typeof starsRating !== "number") return undefined;
	if (typeof numRatings !== "number" || numRatings <= 0) return undefined;

	return { value: starsRating, count: numRatings };
}

export function toListingProduct(
	product: CatalogProduct,
	locale: Locale
): ListingProduct {
	const image = pickImage(product);

	return {
		sku: product.sku,
		title: product.title,
		href: product.metaData?.canonicalUrl?.href ?? `/shop/${product.sku}`,
		image: {
			src: image?.url ?? MISSING_IMAGE_SRC,
			alt: image?.altText ?? product.title
		},
		price: pickPrice(product, locale),
		rating: pickRating(product),
		highlightTag: pickHighlightTag(product),
		isPurchasable: isPurchasable(product.onlineStatus)
	};
}
